import type { ClientMessage, ProfileId, ServerMessage, TranslationDirection, TranslationMode } from '@cixing/shared';
import { backoffDelayWithJitter } from './backoff.js';

/**
 * 自社サーバーとの WebSocket 接続。
 *
 * 再接続の方針(現地の回線品質を前提とした判断):
 *  - 切断中の音声は**捨てる**。貯めて後で送ると、古い音声が今の字幕として現れる。
 *    欠落より重複・ズレの方が講義では有害。
 *  - 再接続後は上流セッションを張り直す(上流は再開できない)。
 *    ただし画面上の字幕履歴は消さない。
 */

export type ConnectionState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface WsClientCallbacks {
  onMessage: (message: ServerMessage) => void;
  onStateChange: (state: ConnectionState) => void;
  /** 再接続で新しいセッションが始まったとき。履歴に区切りを入れるために使う。 */
  onReconnected: () => void;
}

const PING_INTERVAL_MS = 10_000; // 死活検知の高速化

export class TranslationWsClient {
  #socket: WebSocket | undefined;
  #state: ConnectionState = 'idle';
  #attempt = 0;
  #reconnectTimer: number | undefined;
  #pingTimer: number | undefined;
  #shouldRun = false;
  #hasConnectedOnce = false;
  #profiles: ProfileId[] = [];
  #clientSessionId = '';
  #mode: TranslationMode = 'speed';
  #direction: TranslationDirection = 'zh-ja';
  #device: { deviceId?: string; deviceName?: string } = {};
  /**
   * ホットスペア接続。認証済みのWSを1本予備で開いておき、本線が切れたら
   * ハンドシェイク無しで即昇格させる(越境回線ではTLS+WS確立に数秒かかるため)。
   * スペアはセッションを開始しない(startを送らない)のでAPI費用はかからない。
   * スペアの確立に失敗しても本線の動作には一切影響しない。
   */
  #spare: WebSocket | undefined;
  #spareTimer: number | undefined;

  constructor(private readonly callbacks: WsClientCallbacks) {}

  get state(): ConnectionState {
    return this.#state;
  }

  /** 送信可能か。切断中は音声を捨てるための判定に使う。 */
  get canSendAudio(): boolean {
    return this.#socket?.readyState === WebSocket.OPEN;
  }

  start(
    profiles: ProfileId[],
    clientSessionId: string,
    mode: TranslationMode = 'speed',
    device: { deviceId?: string; deviceName?: string } = {},
    direction: TranslationDirection = 'zh-ja',
  ): void {
    this.#profiles = profiles;
    this.#clientSessionId = clientSessionId;
    this.#mode = mode;
    this.#device = device;
    this.#direction = direction;
    this.#shouldRun = true;
    this.#attempt = 0;
    this.#hasConnectedOnce = false;
    this.#connect();

    // 端末がネットワークに復帰したら、バックオフの待ちを飛ばして即座に試す。
    window.addEventListener('online', this.#handleOnline);
  }

  #handleOnline = (): void => {
    if (!this.#shouldRun) return;
    if (this.#state === 'open' || this.#state === 'connecting') return;
    this.#clearReconnectTimer();
    this.#attempt = 0;
    this.#connect();
  };

  #connect(): void {
    this.#setState(this.#hasConnectedOnce ? 'reconnecting' : 'connecting');

    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = new WebSocket(`${protocol}//${location.host}/ws`);
    // 音声はバイナリで送るため、受信も ArrayBuffer で扱えるようにしておく。
    socket.binaryType = 'arraybuffer';
    this.#socket = socket;

    socket.onopen = () => {
      this.#attempt = 0;
      this.#setState('open');
      this.#send({
        type: 'start',
        profiles: this.#profiles,
        clientSessionId: this.#clientSessionId,
        mode: this.#mode,
        direction: this.#direction,
        deviceId: this.#device.deviceId,
        deviceName: this.#device.deviceName,
      });
      if (this.#hasConnectedOnce) this.callbacks.onReconnected();
      this.#hasConnectedOnce = true;
      this.#startPing();
      this.#armSpare();
    };

    socket.onmessage = (event) => {
      if (typeof event.data !== 'string') return;
      try {
        this.callbacks.onMessage(JSON.parse(event.data) as ServerMessage);
      } catch {
        // 壊れたメッセージ1件でセッションを落とさない。
      }
    };

    socket.onclose = () => {
      this.#stopPing();
      this.#socket = undefined;
      if (!this.#shouldRun) {
        this.#setState('closed');
        return;
      }
      if (this.#promoteSpare()) return; // 開通済みの予備へ即切替(ハンドシェイク省略)
      this.#scheduleReconnect();
    };

    socket.onerror = () => {
      // 詳細は onclose で扱う。ここで閉じると二重処理になる。
    };
  }

  #scheduleReconnect(): void {
    this.#setState('reconnecting');
    const delay = backoffDelayWithJitter(this.#attempt);
    this.#attempt += 1;
    this.#reconnectTimer = window.setTimeout(() => {
      if (this.#shouldRun) this.#connect();
    }, delay);
  }

  /** 音声フレームを送る。切断中は捨てる(貯めない)。 */
  sendAudio(frame: ArrayBuffer): void {
    const socket = this.#socket;
    if (socket?.readyState !== WebSocket.OPEN) return;
    try {
      socket.send(frame);
    } catch {
      // 送信失敗は次のフレームで回復する。1フレーム(50ms)の欠落は許容範囲。
    }
  }

  /** 利用者の停止操作。サーバーへ stop を送ってから閉じる(最後の一文を取りこぼさないため)。 */
  stop(): void {
    this.#shouldRun = false;
    this.#clearReconnectTimer();
    this.#stopPing();
    this.#disarmSpare();
    window.removeEventListener('online', this.#handleOnline);

    const socket = this.#socket;
    if (socket?.readyState === WebSocket.OPEN) {
      this.#send({ type: 'stop' });
      // サーバーが上流の session.finished を待つ時間を与えてから閉じる。
      window.setTimeout(() => socket.close(1000, 'user stop'), 3500);
    } else {
      socket?.close();
    }
    this.#socket = undefined;
    this.#setState('closed');
  }

  #send(message: ClientMessage): void {
    const socket = this.#socket;
    if (socket?.readyState !== WebSocket.OPEN) return;
    try {
      socket.send(JSON.stringify(message));
    } catch {
      // 送信失敗時は onclose で再接続に入る。
    }
  }

  #startPing(): void {
    this.#stopPing();
    this.#pingTimer = window.setInterval(() => {
      this.#send({ type: 'ping', t: Date.now() });
    }, PING_INTERVAL_MS);
  }

  #stopPing(): void {
    if (this.#pingTimer !== undefined) window.clearInterval(this.#pingTimer);
    this.#pingTimer = undefined;
  }

  #clearReconnectTimer(): void {
    if (this.#reconnectTimer !== undefined) window.clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = undefined;
  }

  /** スペアを(まだ無ければ)少し遅らせて開く。失敗しても静かに諦めて後で再試行。 */
  #armSpare(): void {
    if (this.#spare || !this.#shouldRun) return;
    // 本線確立の直後は帯域を本線に譲る。2秒後に予備を張る。
    this.#spareTimer = window.setTimeout(() => {
      if (this.#spare || !this.#shouldRun) return;
      try {
        const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
        const spare = new WebSocket(`${protocol}//${location.host}/ws`);
        spare.binaryType = 'arraybuffer';
        spare.onclose = () => {
          // 予備が死んだら本線に影響させず、静かに張り直す。
          if (this.#spare === spare) {
            this.#spare = undefined;
            if (this.#shouldRun && this.#socket) this.#armSpare();
          }
        };
        spare.onerror = () => {
          // onclose で後始末される。
        };
        this.#spare = spare;
      } catch {
        this.#spare = undefined; // 予備は best-effort。失敗は無視する。
      }
    }, 2000);
  }

  /** 本線切断時にスペアを昇格。成功したら true。 */
  #promoteSpare(): boolean {
    const spare = this.#spare;
    this.#spare = undefined;
    if (!spare || spare.readyState !== WebSocket.OPEN) {
      try {
        spare?.close();
      } catch {
        // 閉じかけは無視。
      }
      return false;
    }

    this.#socket = spare;
    // 昇格した接続にハンドラを付け替える(通常の #connect と同じ配線)。
    spare.onmessage = (event) => {
      if (typeof event.data !== 'string') return;
      try {
        this.callbacks.onMessage(JSON.parse(event.data) as ServerMessage);
      } catch {
        // 壊れた1件で落とさない。
      }
    };
    spare.onclose = () => {
      this.#stopPing();
      this.#socket = undefined;
      if (!this.#shouldRun) {
        this.#setState('closed');
        return;
      }
      if (this.#promoteSpare()) return;
      this.#scheduleReconnect();
    };

    this.#attempt = 0;
    this.#setState('open');
    this.#send({
      type: 'start',
      profiles: this.#profiles,
      clientSessionId: this.#clientSessionId,
      mode: this.#mode,
      direction: this.#direction,
      deviceId: this.#device.deviceId,
      deviceName: this.#device.deviceName,
    });
    this.callbacks.onReconnected();
    this.#startPing();
    this.#armSpare(); // 次の予備を補充
    return true;
  }

  #disarmSpare(): void {
    if (this.#spareTimer !== undefined) window.clearTimeout(this.#spareTimer);
    this.#spareTimer = undefined;
    try {
      this.#spare?.close();
    } catch {
      // 閉じかけは無視。
    }
    this.#spare = undefined;
  }

  #setState(state: ConnectionState): void {
    if (this.#state === state) return;
    this.#state = state;
    this.callbacks.onStateChange(state);
  }
}
